/**
 * Core Toast / Notification Utility
 *
 * Standardized toast wrapper around `sonner` with enterprise presets,
 * consistent duration, position, and action buttons.
 * Import `notify` and call it — no component rendering needed.
 *
 * @example
 *   import { notify } from '@/components/core';
 *   notify.success('Data source created successfully');
 *   notify.error('Failed to save', { description: 'Network error — retry in 30s' });
 *   notify.promise(fetchData(), { loading: 'Fetching...', success: 'Done', error: 'Failed' });
 */
import React from 'react';
import { toast, Toaster } from 'sonner';

export interface NotifyOptions {
  /** Secondary description text */
  description?: string;
  /** Duration in ms (default: 4000) */
  duration?: number;
  /** Action button */
  action?: { label: string; onClick: () => void };
  /** Cancel button */
  cancel?: { label: string; onClick: () => void };
  /** Custom id to prevent duplicates */
  id?: string | number;
  /** Dismiss on click */
  dismissible?: boolean;
}

export interface NotifyPromiseMessages {
  loading: string;
  success: string | ((data: unknown) => string);
  error: string | ((error: unknown) => string);
}

const DEFAULT_DURATION = 4000;

/**
 * Enterprise notification utility.
 * Wraps sonner's toast with consistent defaults and typed API.
 */
export const notify = {
  success(message: string, options?: NotifyOptions) {
    return toast.success(message, {
      description: options?.description,
      duration: options?.duration ?? DEFAULT_DURATION,
      action: options?.action ? { label: options.action.label, onClick: options.action.onClick } : undefined,
      cancel: options?.cancel ? { label: options.cancel.label, onClick: options.cancel.onClick } : undefined,
      id: options?.id,
      dismissible: options?.dismissible ?? true,
    });
  },

  error(message: string, options?: NotifyOptions) {
    return toast.error(message, {
      description: options?.description,
      duration: options?.duration ?? 6000,
      action: options?.action ? { label: options.action.label, onClick: options.action.onClick } : undefined,
      cancel: options?.cancel ? { label: options.cancel.label, onClick: options.cancel.onClick } : undefined,
      id: options?.id,
      dismissible: options?.dismissible ?? true,
    });
  },

  warning(message: string, options?: NotifyOptions) {
    return toast.warning(message, {
      description: options?.description,
      duration: options?.duration ?? 5000,
      action: options?.action ? { label: options.action.label, onClick: options.action.onClick } : undefined,
      id: options?.id,
      dismissible: options?.dismissible ?? true,
    });
  },

  info(message: string, options?: NotifyOptions) {
    return toast.info(message, {
      description: options?.description,
      duration: options?.duration ?? DEFAULT_DURATION,
      action: options?.action ? { label: options.action.label, onClick: options.action.onClick } : undefined,
      id: options?.id,
      dismissible: options?.dismissible ?? true,
    });
  },

  /** Loading toast — returns dismiss fn */
  loading(message: string, options?: Pick<NotifyOptions, 'description' | 'id'>) {
    return toast.loading(message, {
      description: options?.description,
      id: options?.id,
    });
  },

  /** Promise-based toast — auto transitions loading → success/error */
  promise<T>(
    promise: Promise<T>,
    messages: NotifyPromiseMessages,
    options?: Pick<NotifyOptions, 'id'>,
  ) {
    return toast.promise(promise, {
      loading: messages.loading,
      success: messages.success as string,
      error: messages.error as string,
      id: options?.id,
    });
  },

  /** Dismiss a specific toast or all */
  dismiss(id?: string | number) {
    if (id) toast.dismiss(id);
    else toast.dismiss();
  },
};

/**
 * Pre-configured Sonner Toaster — mount ONCE at the app root.
 *
 * @example
 *   // main.tsx
 *   import { AppToaster } from '@/components/core';
 *   <App />
 *   <AppToaster />
 */
const TOAST_HOVER_STYLE = `
  [data-sonner-toast] [data-close-button] {
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  [data-sonner-toast]:hover [data-close-button] {
    opacity: 1;
  }
`;

export const AppToaster: React.FC = () => (
  <>
    <style>{TOAST_HOVER_STYLE}</style>
    <Toaster
      position="top-right"
      expand={false}
      richColors
      closeButton
      duration={DEFAULT_DURATION}
      toastOptions={{
        style: {
          fontFamily: 'var(--font-family, Inter, sans-serif)',
          fontSize: '0.875rem',
          borderRadius: 'var(--border-radius, 8px)',
        },
      }}
    />
  </>
);

export default notify;
